import React, { useState } from 'react';
import {
  Alert, Badge, Button, Form, InputGroup, Spinner,
} from 'react-bootstrap';
import Error from '../../../Components/Error';
import CounterpartyPicker, { KIND_LABELS } from '../../../Components/CounterpartyPicker';
import KnowledgeBaseService, {
  SCOPES, scopeNeedsCounterparty,
} from '../../../Services/KnowledgeBaseService';

/**
 * "Did my document get indexed, and what comes back?"
 *
 * Deliberately no model: the chunks are shown as the index returned them,
 * with their relevance scores and the filename they came from. An answer that
 * reads well can hide a retrieval that returned nothing useful — the model
 * paraphrases the gap away — so this tool exists next to the chat rather than
 * instead of it. If a chunk is not here, no amount of prompting will get it
 * into an answer.
 *
 * Scoping to a counterparty is on the same contract as the upload, and a
 * scoped retrieval is EXPECTED to return the general documents too. Each
 * chunk is therefore tagged with which of the two it is: "your contract says"
 * and "our standard policy is" are different answers and an operator has to
 * be able to tell them apart.
 */

const DEFAULT_LIMIT = 10;

/** 0.7421 → "0.74". A score the index did not send reads as "—". */
function formatScore(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(3);
}

/** Green for a strong match, amber mid, grey weak — at a glance, per chunk. */
function scoreVariant(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return 'secondary';
  if (n >= 0.6) return 'success';
  if (n >= 0.35) return 'warning';
  return 'secondary';
}

/**
 * Whether a chunk came from the counterparty's own documents or the general
 * pool. The contract does not put a `scope` on a chunk, so this is read off
 * the `source_uri`, which carries the S3 prefix the document was uploaded
 * under. Unknown rather than guessed when the uri says nothing.
 */
export function chunkOrigin(chunk, counterpartyId) {
  const uri = String(chunk?.source_uri || '');
  if (counterpartyId && uri.includes(counterpartyId)) return 'counterparty';
  if (/\/general\//.test(uri) || /(^|\/)general(\/|$)/.test(uri)) return 'general';
  return null;
}

function RetrievalPreview({ knowledgeBase }) {
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [scope, setScope] = useState('');
  const [counterpartyId, setCounterpartyId] = useState('');
  const [chunks, setChunks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastQuery, setLastQuery] = useState('');

  const kbId = knowledgeBase?.id || '';
  const needsCounterparty = scopeNeedsCounterparty(scope);
  const missingCounterparty = needsCounterparty && !counterpartyId;
  const canRun = Boolean(kbId) && query.trim().length > 0 && !missingCounterparty && !loading;

  const handleRun = async (e) => {
    if (e) e.preventDefault();
    if (!canRun) return;
    setLoading(true);
    setError('');
    const asked = query.trim();
    try {
      const result = await KnowledgeBaseService.retrieve({
        kbId,
        query: asked,
        limit: Number(limit) || DEFAULT_LIMIT,
        counterpartyId: needsCounterparty ? counterpartyId : undefined,
      });
      // A stable React key is stamped on at fetch time: two chunks from the
      // same document can carry the same uri and the same score, so nothing
      // in the payload identifies a row on its own, and the list order is
      // the ranking — not something to re-key on as it changes.
      setChunks((Array.isArray(result) ? result : []).map((chunk, i) => ({
        ...chunk,
        rowKey: `${asked}#${i}`,
      })));
      setLastQuery(asked);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Retrieval failed');
      setChunks(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p className="text-muted small">
        The raw chunks the index returns, with their relevance scores and the
        document each came from — no model in the loop to paraphrase a gap
        away. If a passage is not here, no answer will contain it.
      </p>

      <Error error={error} setError={setError} />

      <Form onSubmit={handleRun}>
        <InputGroup className="mb-3">
          <Form.Control
            type="text"
            value={query}
            onChange={(ev) => setQuery(ev.target.value)}
            placeholder="e.g. how can I report a no-show?"
            aria-label="Retrieval query"
          />
          <Button type="submit" variant="primary" disabled={!canRun}>
            {loading ? (
              <>
                <i className="fa fa-spinner fa-spin me-2" aria-hidden="true" />
                Retrieving…
              </>
            ) : (
              <>
                <i className="fa fa-magnifying-glass me-2" aria-hidden="true" />
                Retrieve
              </>
            )}
          </Button>
        </InputGroup>

        <div className="row g-2 align-items-end mb-3">
          <div className="col-6 col-md-3">
            <Form.Group controlId="kb-retrieve-limit">
              <Form.Label className="small text-muted mb-1">Chunks</Form.Label>
              <Form.Select value={limit} onChange={(ev) => setLimit(ev.target.value)}>
                {[5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
              </Form.Select>
            </Form.Group>
          </div>
          <div className="col-6 col-md-3">
            <Form.Group controlId="kb-retrieve-scope">
              <Form.Label className="small text-muted mb-1">Ask as</Form.Label>
              <Form.Select
                value={scope}
                onChange={(ev) => {
                  setScope(ev.target.value);
                  setCounterpartyId('');
                }}
              >
                <option value="">Nobody in particular</option>
                <option value={SCOPES.OPERATOR}>An operator</option>
                <option value={SCOPES.PARTNER}>A partner</option>
              </Form.Select>
            </Form.Group>
          </div>
          {needsCounterparty && (
            <div className="col-12 col-md-6">
              <CounterpartyPicker
                kind={scope}
                value={counterpartyId}
                onChange={(id) => setCounterpartyId(id)}
                id={`kb-retrieve-${scope}`}
                label={null}
                isInvalid={missingCounterparty}
                feedback={`Pick which ${KIND_LABELS[scope].toLowerCase()} to retrieve as.`}
              />
            </div>
          )}
        </div>
      </Form>

      {loading && (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" size="sm" />
          <span className="text-muted ms-2">Querying the index…</span>
        </div>
      )}

      {!loading && chunks !== null && chunks.length === 0 && (
        <Alert variant="warning">
          <strong>Nothing came back</strong>
          {` for “${lastQuery}”. `}
          Either no document covers it, or the last ingestion has not picked up
          what was uploaded — check the ingestion status, then re-run it.
        </Alert>
      )}

      {!loading && chunks !== null && chunks.length > 0 && (
        <>
          <div className="text-muted small mb-2">
            {`${chunks.length} chunk${chunks.length === 1 ? '' : 's'} for “${lastQuery}”`}
            {needsCounterparty && ' · their documents and the general ones together'}
          </div>
          {chunks.map((chunk) => {
            const origin = chunkOrigin(chunk, needsCounterparty ? counterpartyId : '');
            return (
              <div className="kb-chunk" key={chunk.rowKey}>
                <div className="kb-chunk-head">
                  <Badge bg={scoreVariant(chunk.score)}>{formatScore(chunk.score)}</Badge>
                  <strong>{chunk.source_filename || 'unknown file'}</strong>
                  {origin === 'counterparty' && (
                    <Badge bg="info" title="From this counterparty's own documents">
                      Theirs
                    </Badge>
                  )}
                  {origin === 'general' && (
                    <Badge bg="secondary" title="From the general documents">
                      General
                    </Badge>
                  )}
                </div>
                <p className="kb-chunk-text">{chunk.text}</p>
                {chunk.source_uri && (
                  <div className="kb-chunk-uri" title={chunk.source_uri}>{chunk.source_uri}</div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

export default RetrievalPreview;
